import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { VariableChunkEditor } from './VariableChunkEditor';
import type { Chunk, VariableOption } from './VariableChunkEditor';
import { buildUrlVariables } from '@/lib/urlVariables';

function parseToChunks(value: string, variables: VariableOption[]): Chunk[] {
  if (!value) return [{ type: 'text', value: '', id: crypto.randomUUID() }];

  const escaped = variables.map((v) => v.value.replace(/[{}]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`);
  const parts = value.split(pattern);

  const valueToLabel = new Map(variables.map((v) => [v.value, v.label]));

  const chunks: Chunk[] = parts.map((part) =>
    valueToLabel.has(part)
      ? { type: 'field', value: part, label: valueToLabel.get(part)!, id: crypto.randomUUID() }
      : { type: 'text', value: part, id: crypto.randomUUID() },
  );

  if (chunks.length === 0 || chunks[chunks.length - 1].type === 'field') {
    chunks.push({ type: 'text', value: '', id: crypto.randomUUID() });
  }
  if (chunks[0].type === 'field') {
    chunks.unshift({ type: 'text', value: '', id: crypto.randomUUID() });
  }

  return chunks;
}

function serializeChunks(chunks: Chunk[]): string {
  return chunks.map((c) => c.value).join('');
}

interface UrlVariableInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function UrlVariableInput({ label, value, onChange, className }: UrlVariableInputProps) {
  const { t } = useTranslation('createWebinar');
  const variables: VariableOption[] = buildUrlVariables(t);

  const [chunks, setChunks] = useState<Chunk[]>(() => parseToChunks(value, variables));
  const lastSerializedRef = useRef(value);

  useEffect(() => {
    if (value !== lastSerializedRef.current) {
      setChunks(parseToChunks(value, variables));
      lastSerializedRef.current = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const notify = (newChunks: Chunk[]) => {
    const serialized = serializeChunks(newChunks);
    lastSerializedRef.current = serialized;
    onChange(serialized);
  };

  const handleTextChange = (chunkId: string, val: string) => {
    const next = chunks.map((c) =>
      c.id === chunkId && c.type === 'text' ? { ...c, value: val } : c,
    );
    setChunks(next);
    notify(next);
  };

  const handleAddVariable = (variable: VariableOption) => {
    const fieldId = crypto.randomUUID();
    const textId = crypto.randomUUID();
    const last = chunks[chunks.length - 1];
    let next = [...chunks];
    if (last?.type === 'text' && last.value === '' && chunks.length === 1) {
      next = [];
    }
    next = [
      ...next,
      { type: 'field', value: variable.value, label: variable.label, id: fieldId },
      { type: 'text', value: '', id: textId },
    ];
    setChunks(next);
    notify(next);
  };

  const handleFieldRemove = (fieldId: string) => {
    const idx = chunks.findIndex((c) => c.id === fieldId);
    if (idx === -1) return;
    const before = chunks[idx - 1];
    const after = chunks[idx + 1];
    let next = [...chunks];
    if (before?.type === 'text' && after?.type === 'text') {
      const merged: Chunk = { type: 'text', value: before.value + after.value, id: before.id };
      next.splice(idx - 1, 3, merged);
    } else {
      next.splice(idx, 1);
    }
    if (!next.some((c) => c.type === 'text')) {
      next.push({ type: 'text', value: '', id: crypto.randomUUID() });
    }
    setChunks(next);
    notify(next);
  };

  const handleBackspaceOnEmpty = (chunkId: string) => {
    const idx = chunks.findIndex((c) => c.id === chunkId && c.type === 'text');
    if (idx <= 0) return;
    const prev = chunks[idx - 1];
    if (prev?.type === 'field') handleFieldRemove(prev.id);
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <p className="text-xs text-tertiary px-1">{label}</p>
      <VariableChunkEditor
        chunks={chunks}
        onTextChange={handleTextChange}
        onFieldRemove={handleFieldRemove}
        onBackspaceOnEmpty={handleBackspaceOnEmpty}
        onAddVariable={handleAddVariable}
        variables={variables}
        placeholder="https://..."
        hint={t('urlVariable.hint')}
      />
    </div>
  );
}
